import React, { useState, useEffect } from 'react';
import './App.css';
import './site.css';
import { LEGAL_PAGES } from './legal';
import { ESPACE_ROUTES, operatorAppUrl } from './espace';
import Home from './site/Home';
import {
  HowPage, MutualAidPage, SecurityPage, CollectivitesPage, PoliceMunicipalePage, DataPage, FaqPage,
  ContactPage, AccessibilityPage, LegalPage, NotFoundPage,
} from './site/Pages';
import { useHead } from './site/seo';
import { useRevealEffect } from './site/Layout';

/* Espace utilisateur : pages du site (/connexion, /inscription, /mon-compte…)
   branchées sur l'API — le back-office /app reste réservé à l'administrateur
   (aucun lien public vers lui). */

/* Cookies : le site ne pose AUCUN cookie ni traceur (pas d'analytics) — la bannière de
   consentement a été retirée le 2026-08-31 (CNIL : pas de bandeau sans traceur soumis à consentement). */

/* Pages statiques du site vitrine (prerendues au build — scripts/prerender.js). */
const SITE_PAGES = {
  '/comment-ca-marche': HowPage,
  '/entraide-citoyenne': MutualAidPage,
  '/securite-citoyenne': SecurityPage,
  '/collectivites': CollectivitesPage,
  '/police-municipale': PoliceMunicipalePage,
  '/confidentialite-donnees': DataPage,
  '/faq': FaqPage,
  '/contact': ContactPage,
  '/accessibilite': AccessibilityPage,
};

export function normalizePath(p) {
  if (!p) return '/';
  let path = p.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  return path || '/';
}

/** Rendu d'une route (utilisé tel quel par le prerender, sans état client). */
export function renderRoute(route, { hasSession = false, storedRole = '' } = {}) {
  if (LEGAL_PAGES[route]) return <LegalPage slug={route} />;
  const Espace = ESPACE_ROUTES[route];
  if (Espace) return <Espace />;
  if (route === '/' && hasSession) {
    const Urgence = ESPACE_ROUTES['/urgence'];
    return <Urgence />;
  }
  if (route === '/') return <Home />;
  const Page = SITE_PAGES[route];
  if (Page) return <Page />;
  return <NotFoundPage route={route} />;
}

/* ========== MAIN APP WITH ROUTING ========== */
export default function App({ initialPath }) {
  const [route, setRoute] = useState(() => normalizePath(initialPath || (typeof window !== 'undefined' ? window.location.pathname : '/')));
  // Session lue APRÈS l'hydratation (le HTML prerendu = visiteur anonyme) :
  // évite tout décalage serveur/client.
  const [session, setSession] = useState({ hasSession: false, storedRole: '', storedUser: null });

  useEffect(() => {
    const handleNav = () => setRoute(normalizePath(window.location.pathname));
    window.addEventListener('popstate', handleNav);
    return () => window.removeEventListener('popstate', handleNav);
  }, []);

  useEffect(() => {
    try {
      const hasSession = !!localStorage.getItem('civik_access_token');
      const storedUser = JSON.parse(localStorage.getItem('civik_user') || 'null');
      setSession({ hasSession, storedRole: (storedUser || {}).role || '', storedUser });
    } catch { /* stockage bloqué */ }
  }, [route]);

  // Interception des liens internes (navigation client) — hors /app, /api,
  // /surete, /securite (autres applications sur la même origine).
  useEffect(() => {
    const handleClick = (e) => {
      const a = e.target.closest('a');
      if (!a) return;
      if (a.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      const href = a.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('//')) return;
      if (/^\/(app|api|surete|securite|static|fonts)(\/|$)/.test(href)) return;
      if (/\.[a-z0-9]{2,5}$/i.test(href.split('#')[0].split('?')[0])) return; // fichier (pdf, png…)
      e.preventDefault();
      const [pathPart, hash] = href.split('#');
      const next = normalizePath(pathPart || window.location.pathname);
      if (next !== normalizePath(window.location.pathname)) {
        window.history.pushState({}, '', href);
        setRoute(next);
        if (!hash) window.scrollTo(0, 0);
      } else if (hash) {
        window.history.replaceState({}, '', href);
      }
      if (hash) {
        requestAnimationFrame(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Ancre présente au chargement direct d'une URL (ex. /#faq depuis un lien externe).
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const t = setTimeout(() => { const el = document.getElementById(hash); if (el) el.scrollIntoView({ block: 'start' }); }, 50);
    return () => clearTimeout(t);
  }, [route]);

  const isEspace = !!ESPACE_ROUTES[route];
  useHead(isEspace ? route : (LEGAL_PAGES[route] || SITE_PAGES[route] || route === '/' ? route : '/404'));
  useRevealEffect(route);

  // Opérateur police / secours (2026-09-14) : la web app de son service.
  useEffect(() => {
    if (route === '/' && session.hasSession && session.storedRole === 'AGENCY_OPERATOR') {
      window.location.replace(operatorAppUrl(session.storedUser));
    }
  }, [route, session]);

  return renderRoute(route, session);
}
